import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/named
import { HeroLmpSlim, IHeroLmpSlim, MatchTypings } from '@sky-it-livedata/livedata-sports-design-system';
import { matchHeroSlimWidget, currentDatetimeWidget } from '@sky-it-livedata/livedata-live-match-pages-widgets';

import TimerWorker, { TimerEvent } from '../../utils/web.worker';
import { IModelWrapper } from '../../typings/common-types';
import { ICurrentDateTime } from '../../typings/match-hero';
import { isEmptyObject } from '../../utils/props-validator';
import {
  // chooseMatchCurrentDateTime,
  computeMatchCurrentDateTime,
  fromDateToSeconds,
  ModelCurrentDateTime,
  ModelHeroLmp,
  render,
} from '../../utils/match-hero-helpers';

export interface IHeroSlimWrapper extends IModelWrapper<IHeroLmpSlim> {}

// WEB WORKER - Defined HERE to NOT include it in React component USE-EFFECT DEPS
const timerWorker = new (TimerWorker as any)(); // FIXME: to avoid error "Type 'Worker' has no construct signatures. ts(2351)"

const HeroSlimWrapper = ({ query, initialModel, live }: IHeroSlimWrapper) => {
  // Component INTERNAL STATE - Will RE-RENDER when updated PAYLOAD (QUERY/SUBSCRIPTIONS) or TIMER TICK are SET
  const [payload, updatePayload] = useState<IHeroLmpSlim>(initialModel);
  const [interval, updateInterval] = useState<number | null>(null);

  // WEB WORKER
  timerWorker.onmessage = (event: MessageEvent) => {
    const { key, value } = event.data as TimerEvent;

    switch (key) {
      case 'timerInterval': // TIMER TICK from WEB-WORKER
        updateInterval(value); // Force RE-RENDER
        break;

      default:
        break;
    }
  };

  // 1st SETUP - Store TIME DIFFERENCE between SERVER and CLIENT
  const systemDate = new Date().toISOString();
  const timeDiffServerToClientRef = useRef<ICurrentDateTime>({
    remoteCurrentDateTime: systemDate,
    remoteCurrentDateTimeSeconds: fromDateToSeconds(systemDate),
    systemCurrentDateTime: systemDate,
    systemCurrentDateTimeSeconds: fromDateToSeconds(systemDate),
    timeDiff: 0,
    matchCurrentDateTime: systemDate,
    matchCurrentDateTimeSeconds: fromDateToSeconds(systemDate),
  });

  const isTimerRunningRef = useRef(false);

  // Run QUERIES/SUBS with USESERVICE
  const heroLmp: IHeroLmpSlim = ModelHeroLmp(matchHeroSlimWidget, initialModel, live, query);
  const matchCurrentDateTime: string | null = ModelCurrentDateTime(currentDatetimeWidget);

  // Calc TIME DIFFERENCE between SERVER and CLIENT
  useEffect(() => {
    if (matchCurrentDateTime) {
      // Set REMOTE, SYSTEM and compute CURRENT DATE/TIME using REMOTE-SYSTEM diff
      const remoteCurrentDateTimeSeconds: number = fromDateToSeconds(matchCurrentDateTime);
      const systemCurrentDateTime: string = new Date().toISOString();
      const systemCurrentDateTimeSeconds: number = fromDateToSeconds(systemCurrentDateTime);
      const timeDiff: number = systemCurrentDateTimeSeconds - remoteCurrentDateTimeSeconds; // NEGATIVE = SERVER TIME is AHEAD of CLIENT, e.g. 14:01:30 instead of 14:00:55
      const matchCurrentDateTimeSeconds: number = systemCurrentDateTimeSeconds - timeDiff; // Should be EQUAL to "remoteCurrentDateTimeSeconds"
      const matchCurrentDateTimeComputed: string = new Date(matchCurrentDateTimeSeconds * 1000).toISOString(); // Should be EQUAL to "remoteCurrentDateTime"

      timeDiffServerToClientRef.current = {
        remoteCurrentDateTime: matchCurrentDateTime,
        remoteCurrentDateTimeSeconds,
        systemCurrentDateTime,
        systemCurrentDateTimeSeconds,
        timeDiff,
        matchCurrentDateTime: matchCurrentDateTimeComputed,
        matchCurrentDateTimeSeconds,
      };
    }
  }, [matchCurrentDateTime]);

  // TIMER -> RENDER - INTERVAL is UPDATED by WORKER.ONMESSAGE or RESETTED by MATCH STATUS
  useEffect(() => {
    // Updates MATCH DATE/TIME using SYSTEM DATE/TIME + TIME DIFF computed on 1st RENDER
    timeDiffServerToClientRef.current = {
      ...timeDiffServerToClientRef.current,
      ...computeMatchCurrentDateTime(timeDiffServerToClientRef),
    };

    render(heroLmp, timeDiffServerToClientRef.current.matchCurrentDateTime, 0, updatePayload); // FIXME: no "interval" passed, now we UPDATE MATCH CURRENT DATE/TIME every TIMER TICK!
  }, [interval]); // eslint-disable-line react-hooks/exhaustive-deps

  // SUBSCRIPTION -> RENDER
  useEffect(() => {
    /**
     * When HERO-LMP changes:
     * - START TIMER if MATCH is [1T,2T,1TS,2TS] + set "isTimerRunningRef" to TRUE
     * - else CLEAR TIMER + CURRENT INTERVAL + set "isTimerRunningRef" to FALSE
     */
    if (!isEmptyObject(heroLmp) && heroLmp.match && timeDiffServerToClientRef.current.matchCurrentDateTime) {
      // START TIMER if MATCH is [1T,2T,1TS,2TS]
      if (MatchTypings.hasTimerArray.includes(heroLmp.match.status) && !isTimerRunningRef.current) {
        // WEB WORKER - MESSAGE SEND
        updateInterval(0); // 1st RUN of the TIMER CODE
        timerWorker.postMessage({ key: 'timerSet', value: 20 } as TimerEvent); // SECONDS, not MILLISECONDS
        isTimerRunningRef.current = true;
        // FIXME: UPDATE TIMERS + field "statusStartDateTime"???
      } else if (!MatchTypings.hasTimerArray.includes(heroLmp.match.status) && isTimerRunningRef.current) {
        // CLEAR TIMER + CURRENT INTERVAL + set "isTimerRunningRef" to FALSE
        timerWorker.postMessage({ key: 'timerClear' } as TimerEvent);
        isTimerRunningRef.current = false;
        updateInterval(null); // RESET interval
        // FIXME: In these MATCH STATUSES field "statusStartDateTime" is NOT updated from BE!
      }

      render(heroLmp, timeDiffServerToClientRef.current.matchCurrentDateTime, 0, updatePayload); // FIXME: no "interval" passed, now we UPDATE MATCH CURRENT DATE/TIME every TIMER TICK!
    }
  }, [heroLmp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Choose if REMOTE DATE/TIME is reliable (comparing it to STATUS START DATE/TIME, to avoid NEGATIVE TIMING)
  // FIXME: after the FIX of updateInterval(null) vs updateInterval(0) in heroLmp useEffect, THIS WA SHOULDN'T BE NECESSARY ANYMORE!
  // useEffect(() => {
  //   // Executed only once, so "interval === 0"
  //   const statusStartDateTime: string | null = heroLmp?.match?.statusStartDateTime ?? null;
  //   if (matchCurrentDateTime && statusStartDateTime && interval === 0) {
  //     chooseMatchCurrentDateTime(statusStartDateTime, timeDiffServerToClientRef, interval, updateInterval); // FIXME
  //   }
  // }, [matchCurrentDateTime, heroLmp, interval]); // eslint-disable-line react-hooks/exhaustive-deps

  if (typeof payload === 'undefined' || (typeof payload === 'object' && isEmptyObject(payload))) {
    return null; // No LMP HERO data, no WIDGET to render
  }

  return <div>{<HeroLmpSlim {...payload} />}</div>;
};

export default HeroSlimWrapper;
